import moment from "moment";
import { useState, useCallback, useEffect } from "react";
import { Field, reduxForm } from "redux-form";
import { Button, Input, Label } from "expano-components";

import { Main, Dashboard, Select, DatePicker, PageLoader } from "components";

import useGet from "hooks/useGet";
import useCreate from "hooks/useCreate";

import channel_campaign_statuses_options from "utils/select_options/channel_campaign_statuses_options";
import channel_campaign_group_kind_options from "utils/select_options/channel_campaign_group_kind_options";

import { ReactComponent as ProductIcon } from "icons/product.svg";

import CampaignProducts from "./components/CampaignProducts";

const InputField = (props) => <Input {...props} />;
const SelectField = (props) => <Select {...props} />;
const MultiSelectField = (props) => <Select isMulti {...props} />;
const DatePickerField = (props) => <DatePicker {...props} />;

const placement_ids_options = [
  { label: "Wyniki wyszukiwania", value: "listing" },
  { label: "Lewa kolumna", value: "leftpanel" },
  { label: "Strona produktu", value: "showitem" },
  { label: "Pozostałe miejsca emisji", value: "mainpage" },
  { label: "Sieć reklamowa - Google Ads", value: "google" },
];

const convertValues = (values) => {
  const data = { ...values };
  if (data.display_start_date) {
    data.display_start_date = moment(values.display_start_date).format(
      "DD-MM-YYYY",
    );
  }

  if (data.display_end_date) {
    data.display_end_date = moment(values.display_end_date).format(
      "DD-MM-YYYY",
    );
  }

  return data;
};

const ChannelCampaignGroupNew = ({
  handleSubmit,
  change,
  pristine,
  history,
  invalid,
  match: {
    params: { channel_id, channel_campaign_id },
  },
}) => {
  const [product_ids, setProductIds] = useState([]);

  const { data: channel_campaign, is_loading } = useGet({
    query: ["channel_campaign", channel_campaign_id],
    path: `/channels/${channel_id}/channel_campaigns/${channel_campaign_id}`,
    context: "ui_create_channel_campaign_group",
    error_message_enabled: false,
    onError: () => history.push(`/campaigns/channels/${channel_id}`),
  });

  const { is_creating, create } = useCreate({
    path: `/channels/${channel_id}/channel_campaigns/${channel_campaign_id}/channel_campaign_groups`,
    onSuccess: ({ id }) => {
      history.push(
        `/campaigns/channels/${channel_id}/channel_campaigns/${channel_campaign_id}/channel_campaign_groups/${id}`,
      );
    },
  });

  useEffect(() => {
    change("display_start_date", new Date());
  }, []);

  const handleRemoveProduct = useCallback(
    (id) => {
      setProductIds(product_ids.filter((product_id) => product_id !== id));
    },
    [product_ids, setProductIds],
  );

  const handleAddProducts = useCallback(
    (ids) => {
      setProductIds([...new Set([...product_ids, ...ids])]);
    },
    [product_ids, setProductIds],
  );

  const onSubmit = (values) =>
    create({ ...convertValues(values), product_ids });

  if (is_loading) {
    return <PageLoader />;
  }

  return (
    <Main
      page={{
        name: "Dodaj kampanię",
        icon: <ProductIcon />,
        breadcrumbs: [
          {
            name: "Kampanine reklamowe",
            path: `/campaigns/channels/${channel_id}`,
          },
          { name: "Dodaj kampanię" },
        ],
      }}
    >
      <Dashboard
        header={{
          title: (
            <>
              <Label
                type="double"
                left={{
                  text: channel_campaign?.channel?.shop?.label_text,
                  color: `#${channel_campaign?.channel?.shop?.label_color}`,
                }}
                right={{
                  text: channel_campaign?.channel?.platform?.label_text,
                  color: `#${channel_campaign?.channel?.platform?.label_color}`,
                }}
              />
              <span>{channel_campaign?.channel?.identifier}</span>
            </>
          ),
          aside: (
            <div className="df" style={{ gap: "10px" }}>
              <Button
                type="cancel"
                onClick={() =>
                  history.push(`/campaigns/channels/${channel_id}`)
                }
              />
              <Button
                type="save"
                text={is_creating ? "Proszę czekać..." : "Utwórz"}
                disabled={pristine || invalid || is_creating}
                onClick={handleSubmit(onSubmit)}
              />
            </div>
          ),
        }}
      />
      <div className="dashboard-wrapper-flex">
        <div className="column-half">
          <Dashboard header={{ title: "Kampania", icon: "info" }}>
            <div className="dashboard__form">
              <Input
                label="Kategoria"
                disabled
                meta={{}}
                input={{
                  name: "root_taxon_id",
                  value: channel_campaign?.root_taxon?.id
                    ? `${channel_campaign.root_taxon.name} [${channel_campaign.root_taxon.identifier}]`
                    : "Brak",
                }}
              />
              <Input
                label="Nazwa"
                disabled
                meta={{}}
                input={{
                  name: "campaign_name",
                  value: channel_campaign?.name,
                }}
              />
            </div>
          </Dashboard>
          <Dashboard header={{ title: "Podstawowe informacje", icon: "info" }}>
            <form
              className="dashboard__form"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              <Field
                name="kind"
                label="Rodzaj"
                component={SelectField}
                options={channel_campaign_group_kind_options}
                handleChange={(value) => change("kind", value)}
              />
              <Field
                name="name"
                label="Nazwa"
                component={InputField}
                type="text"
              />
              <Field
                name="status"
                label="Status"
                component={SelectField}
                options={channel_campaign_statuses_options}
                handleChange={(value) => change("status", value)}
              />
              <Field
                name="sales_period_standard_cpc"
                label="Standardowa stawka CPC"
                component={InputField}
                type="number"
                placeholder=">0.65 <10"
              />
              <Field
                name="budget_daily_amount"
                label="Budżet dzienny "
                component={InputField}
                type="number"
              />
              <Field
                name="budget_total_amount"
                label="Budżet całkowity"
                component={InputField}
                type="number"
              />
              <Field
                name="placements"
                label="Miejsce emisji"
                component={MultiSelectField}
                options={placement_ids_options}
                handleChange={(values) => change("placements", values)}
              />
              <div className="row m-t-20">
                <div className="box-6">
                  <Field
                    min_date={new Date()}
                    name="display_start_date"
                    label="Data rozpoczęcia emisji"
                    component={DatePickerField}
                    onChange={(date) => change("display_start_date", date)}
                  />
                </div>
                <div className="box-6">
                  <Field
                    min_date={new Date()}
                    name="display_end_date"
                    label="Data zakończenia emisji"
                    component={DatePickerField}
                    onChange={(date) => change("display_end_date", date)}
                  />
                </div>
              </div>
            </form>
          </Dashboard>
        </div>
        <div className="column-half">
          <CampaignProducts
            channel_id={channel_id}
            product_ids={product_ids}
            root_taxon={channel_campaign?.root_taxon}
            handleRemoveProduct={handleRemoveProduct}
            handleAddProducts={handleAddProducts}
          />
        </div>
      </div>
    </Main>
  );
};

const validate = (values) => {
  const errors = {};

  if (!values.kind) {
    errors.kind = "Rodzaj kampanii jest wymagany!";
  }
  if (!values.name) {
    errors.name = "Nazwa grupy kampanii jest wymagana!";
  }
  if (!values.status) {
    errors.status = "Status kampanii jest wymagany!";
  }
  if (!values.display_start_date) {
    errors.display_start_date = "Start kampanii jest wymagany!";
  }
  if (!values.sales_period_standard_cpc) {
    errors.sales_period_standard_cpc = "Standardowa stawka CPC jest wymagana!";
  }
  if (!values.budget_daily_amount) {
    errors.budget_daily_amount = "Dzienny budżet jest wymagany!";
  }

  return errors;
};

export default reduxForm({
  form: "create-channel-campaign-group",
  enableReinitialize: true,
  validate,
})(ChannelCampaignGroupNew);
